import { Trophy, XCircle, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui';
import { 
  calculateTestOutResults, 
  formatScoreAsPercent, 
  formatThresholdAsPercent 
} from '../../data/testOutScoring';

export function TestOutComplete({
  phaseNum,
  exercises,
  exerciseData,
  injurySide,
  sessionState,
  onFinish,
}) {
  // Calculate results
  const results = calculateTestOutResults(phaseNum, exercises, exerciseData, injurySide);
  const { overallPassed, exerciseResults, summary } = results;

  // Calculate duration
  const startTime = new Date(sessionState.startedAt);
  const endTime = sessionState.completedAt ? new Date(sessionState.completedAt) : new Date();
  const durationMinutes = Math.round((endTime - startTime) / 60000);

  return (
    <div className="flex-1 flex flex-col p-4">
      {/* Result Banner */}
      <div className={`
        rounded-xl p-6 text-center mb-6
        ${overallPassed 
          ? 'bg-green-50 border border-green-200' 
          : 'bg-red-50 border border-red-200'
        }
      `}>
        <div className={`
          h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4
          ${overallPassed ? 'bg-green-100' : 'bg-red-100'}
        `}>
          {overallPassed ? (
            <Trophy className="h-8 w-8 text-green-600" />
          ) : (
            <XCircle className="h-8 w-8 text-red-600" />
          )}
        </div>

        <h2 className={`text-2xl font-bold mb-2 ${overallPassed ? 'text-green-800' : 'text-red-800'}`}>
          {overallPassed ? 'Congratulations!' : 'Not Yet'}
        </h2>
        
        <p className={overallPassed ? 'text-green-700' : 'text-red-700'}>
          {overallPassed 
            ? `You passed the Phase ${phaseNum} test out!`
            : `You didn't pass all exercises this time. Keep training and try again!`
          }
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{summary.passedExercises}</p>
          <p className="text-xs text-gray-500">Passed</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{summary.failedExercises}</p>
          <p className="text-xs text-gray-500">Not Passed</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{durationMinutes}</p>
          <p className="text-xs text-gray-500">Minutes</p>
        </div>
      </div>

      {/* Individual Results */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Exercise Results</h3>
        </div>

        <div className="divide-y divide-gray-100">
          {exercises.map(exercise => {
            const result = exerciseResults[exercise.id];
            if (!result) return null;

            return (
              <div key={exercise.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center">
                  {result.passed ? (
                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center mr-3">
                      <XCircle className="h-4 w-4 text-red-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900">{result.displayName}</p>
                    <p className="text-xs text-gray-500">
                      Required: {formatThresholdAsPercent(result.threshold)}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <p className={`text-lg font-bold ${result.passed ? 'text-green-600' : 'text-red-600'}`}>
                    {exercise.isSidePlank 
                      ? (result.score === 1 ? 'Pass' : 'Fail')
                      : formatScoreAsPercent(result.score)
                    }
                  </p>
                  {exercise.isSidePlank && result.details && (
                    <p className="text-xs text-gray-500">
                      L: {result.details.leftPassed ? '✓' : '✗'} | R: {result.details.rightPassed ? '✓' : '✗'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Next Steps */}
      {!overallPassed && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-amber-800">Keep Going!</h4>
              <p className="text-sm text-amber-700 mt-1">
                Continue with your training sessions to build strength and try the test out again 
                when you're ready. You can retake it as many times as needed.
              </p>
            </div>
          </div>
        </div>
      )}

      {overallPassed && phaseNum < 4 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-blue-800">Phase {phaseNum + 1} Unlocked!</h4>
              <p className="text-sm text-blue-700 mt-1">
                Great work! You've unlocked the next phase of your rehabilitation program.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Done Button */}
      <div className="mt-auto pt-4">
        <Button
          onClick={onFinish}
          fullWidth
          size="lg"
        >
          Done
        </Button>
      </div>
    </div>
  );
}